import { UsergroupDeleteOutlined, UserSwitchOutlined } from '@ant-design/icons';
import {
  BasketballPlayer,
  BasketballTeam,
  CustomTableColumn,
  User,
  UserCustomAttributeType,
} from '@hefe/models';
import {
  BasketballTeamsSlice,
  currentUserSelector,
  ModalsSlice,
  UsersSlice,
} from '@hefe/store';
import { clubHasSubscription, formatDate } from '@hefe/utils';
import { Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import { t } from '@hefe/i18n';
import React, { useEffect, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { useAppDispatch } from '../../../../../store/store';
import CustomTable from '../../../../core/components/table/custom-table.component';
import AddPlayersToTeamForm from '../../../forms/add-players-to-team/add-players-to-team.form';
import TransferPlayersForm from '../../../forms/transfer-players/transfer-players.form';
import PlayersMoreActionsComponent from '../player-more-actions/player-more-actions.component';
import SubscriptionWrapper from '../../../../core/components/subscription-wrapper/subscription-wrapper.component';
import { AuthSubscriptions } from '@hefe/auth';
import { useSelector } from 'react-redux';

interface TeamPlayersComponentProps {
  team: BasketballTeam;
}

export default function TeamPlayersComponent(props: TeamPlayersComponentProps) {
  const { team } = props;

  const dispatch = useAppDispatch();
  const user = useSelector(currentUserSelector);
  const customUserAttributes = useSelector(
    UsersSlice.usersCustomUserAttributesSelector
  );

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPlayers();
    fetchCustomAttributes();
  }, []);

  const fetchPlayers = () => {
    setIsLoading(true);
    dispatch(BasketballTeamsSlice.fetchBasketballTeamPlayers(team.id))
      .unwrap()
      .catch(() => null)
      .finally(() => setIsLoading(false));
  };

  const fetchCustomAttributes = () => {
    if (clubHasSubscription(AuthSubscriptions.third, user)) {
      dispatch(UsersSlice.fetchAllCustomUserAttributes());
    }
  };

  const removePlayers = () => {
    setIsDeleting(true);
    dispatch(
      BasketballTeamsSlice.removePlayersFromBasketballTeam({
        team_id: props.team.id,
        player_ids: selectedUserIds,
      })
    )
      .unwrap()
      .then(() => {
        setSelectedUserIds([]);
      })
      .catch(() => null)
      .finally(() => setIsDeleting(false));
  };

  const openTransferForm = () => {
    dispatch(
      ModalsSlice.modalsActions.openModal({
        title: t('transfer_players'),
        formId: `transfer-players-form-${team.id}`,
        component: (
          <TransferPlayersForm
            team={team}
            selectedPlayerIds={selectedUserIds}
          />
        ),
      })
    );
  };

  const getCustomAttributeColumns = () => {
    const columns: CustomTableColumn[] = [];

    customUserAttributes.forEach((attribute) => {
      const isDateAttribute = attribute.type === UserCustomAttributeType.DATE;
      columns.push({
        key: `custom-attribute-${attribute.id}`,
        title: attribute.name,
        default: false,
        // TODO implement filters/sorter
        //showFilter: !isDateAttribute,
        //showDateFilter: isDateAttribute,
        //onFilter: (value: any, record: any) => true,
        render: (user: User) => {
          let value = '';
          const customUserValue = user.custom_attribute_values?.find(
            (val) => val.attribute_id === attribute.id
          );
          if (customUserValue) {
            value = isDateAttribute
              ? formatDate(customUserValue.value)
              : customUserValue.value;
          }
          return value;
        },
      });
    });

    return columns;
  };

  const columns: CustomTableColumn[] = [
    {
      key: 'firstname',
      title: t('firstname'),
      default: true,
      dataIndex: 'firstname',
      sortType: 'string',
    },
    {
      key: 'lastname',
      title: t('lastname'),
      default: true,
      dataIndex: 'lastname',
      sortType: 'string',
    },
    {
      key: 'birthday',
      title: t('birthday'),
      dataIndex: 'birthday',
      default: false,
      sortType: 'date',
      render: (value: string) => (value ? formatDate(value) : ''),
    },
    {
      key: 'address',
      title: t('address'),
      default: false,
      sortType: 'string',
      render: (item: User) => {
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text>{item.street}</Typography.Text>
            <Typography.Text>
              {item.zipcode} {item.city}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      key: 'email',
      title: t('email'),
      default: false,
      dataIndex: 'email',
      sortType: 'string',
    },
    {
      key: 'phone',
      title: t('phone'),
      default: false,
      dataIndex: 'phone',
      sortType: 'string',
    },
    {
      key: 'tan',
      title: t('tan'),
      default: true,
      sortType: 'string',
      render: (value: BasketballPlayer) =>
        value.team_tan
          ? String(value.team_tan).padStart(3, '0')
          : value.tan
          ? String(value.tan).padStart(3, '0')
          : '---',
    },
    {
      key: 'jersey',
      title: t('jersey_number'),
      dataIndex: 'jersey',
      default: true,
      sortType: 'number',
      render: (value: string) => value || '--',
    },
    {
      key: 'captain',
      title: t('captain'),
      dataIndex: 'captain',
      default: false,
      render: (value: boolean) => (value ? <FaCheck id="fa-check" /> : ''),
    },
    ...getCustomAttributeColumns(),
    {
      key: '...',
      title: '...',
      default: true,
      render: (value: BasketballPlayer) => (
        <PlayersMoreActionsComponent player={value} />
      ),
    },
  ];

  return (
    <CustomTable
      key={`team-players-${team.id}`}
      name="team-players"
      loading={isLoading}
      rowKey="id"
      dataSource={team.players || []}
      columns={columns}
      rowSelection
      onRowSelect={(selectedRowKeys) => setSelectedUserIds(selectedRowKeys)}
      addForm={{
        formId: `add-players-to-team-form-${props.team.id}`,
        component: <AddPlayersToTeamForm team={team} />,
        title: t('add_players'),
      }}
      toolbarItems={[
        <Tooltip title={t('delete_players')}>
          <Popconfirm
            title={t('questions.delete_players')}
            disabled={isDeleting || selectedUserIds.length === 0}
            onConfirm={() => removePlayers()}
          >
            <Button
              loading={isDeleting}
              disabled={selectedUserIds.length === 0}
              icon={<UsergroupDeleteOutlined />}
            />
          </Popconfirm>
        </Tooltip>,
        <SubscriptionWrapper
          subscription={AuthSubscriptions.second}
          title={t('transfer_players')}
        >
          <Tooltip arrow={false} title={t('transfer_players')}>
            <Button
              onClick={() => openTransferForm()}
              disabled={selectedUserIds.length === 0}
              icon={<UserSwitchOutlined />}
            />
          </Tooltip>
        </SubscriptionWrapper>,
      ]}
    />
  );
}
